/**
 * Copyright (c) 2024-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { Suspense, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ContentPlaceholder } from "../../components/ContentPlaceholder";
import { RedirectContext } from "../../components/RedirectContext";
import { RedirectExternal } from "../../components/RedirectExternal";
import { MULTIDISCIPLINARY_SUBJECT_ID } from "../../constants";
import type { GQLTopicPageQuery, GQLTopicPageQueryVariables } from "../../graphqlTypes";
import { getSubjectType } from "../../routeHelpers";
import { findAccessDeniedErrors, hasNotFoundStatus } from "../../util/handleError";
import { LocaleNavigate, useLocalePath, useRawLocation } from "../../util/localePath";
import { constructNewPath, isValidContextId } from "../../util/urlHelper";
import { ForbiddenPage } from "../ErrorPage/ForbiddenPage";
import { MovedResourcePage } from "../MovedResourcePage/MovedResourcePage";
import { NotFoundPage } from "../NotFoundPage/NotFoundPage";
import { MultidisciplinarySubjectArticle } from "./MultidisciplinarySubjectArticle";
import { TopicContainer } from "./TopicContainer";

export const topicPageQuery: TypedDocumentNode<GQLTopicPageQuery, GQLTopicPageQueryVariables> = gql`
  query topicPage($rootId: String, $contextId: String, $transformArgs: TransformedArticleContentInput) {
    node(rootId: $rootId, contextId: $contextId) {
      id
      name
      supportedLanguages
      breadcrumbs
      relevanceId
      nodeType
      article {
        id
        htmlTitle
        htmlIntroduction
        grepCodes
        revision
        metaImage {
          image {
            imageUrl
          }
          alttext {
            alttext
          }
        }
        visualElementEmbed {
          content
        }
      }
      meta {
        metaDescription
        metaImage {
          url
          alt
        }
      }
      context {
        contextId
        rootId
        name
        url
        defaultUrl
        isArchived
        parents {
          id
          name
          url
        }
      }
      ...MovedResourcePage_Node
      ...MultidisciplinarySubjectArticle_Node
      ...TopicContainer_Node
    }
  }
  ${MovedResourcePage.fragments.resource}
  ${MultidisciplinarySubjectArticle.fragments.node}
  ${TopicContainer.fragments.node}
`;

export const TopicPage = () => (
  <Suspense fallback={<ContentPlaceholder />}>
    <TopicPageContent />
  </Suspense>
);

const TopicPageContent = () => {
  const { contextId } = useParams();
  const location = useRawLocation();
  const { i18n } = useTranslation();
  const setRedirect = useContext(RedirectContext);
  const resolveLocalePath = useLocalePath();
  const query = useSuspenseQuery(
    topicPageQuery,
    !isValidContextId(contextId)
      ? skipToken
      : {
          variables: {
            contextId: contextId,
            // TODO: Is it wise to hardcode this? Should it always be set? Multidisciplinary breaks if we don't have it.
            rootId: MULTIDISCIPLINARY_SUBJECT_ID,
            transformArgs: {
              showVisualElement: "true",
              subjectId: MULTIDISCIPLINARY_SUBJECT_ID,
            },
          },
        },
  );

  if (query.error) {
    const accessDeniedErrors = findAccessDeniedErrors(query.error);
    if (accessDeniedErrors.length) {
      const nonRecoverableError = accessDeniedErrors.some(
        (e) => !e.path?.includes("resources") && !e.path?.includes("children"),
      );

      if (nonRecoverableError) return <ForbiddenPage />;
    }

    if (hasNotFoundStatus(query.error)) return <NotFoundPage />;
  }

  const node = query.data?.node;

  const redirectTo = (url: string) => {
    if (setRedirect) {
      setRedirect({ status: 301, url: resolveLocalePath(url) });
      return null;
    }
    return <LocaleNavigate to={url} replace />;
  };

  if (node && node.nodeType !== "TOPIC" && node.nodeType !== "CASE") {
    const url = node.context?.url;
    if (!url) return <NotFoundPage />;
    return redirectTo(url);
  }

  if (!node?.article) {
    return <NotFoundPage />;
  }

  if (i18n.language === "se" && !node.supportedLanguages?.includes("se")) {
    return <RedirectExternal to={constructNewPath(location.pathname, "nb")} />;
  }

  if (node.contexts.length && !node.contexts.some((c) => c.contextId === contextId)) {
    const url = node.contexts[0]?.url;
    if (node.contexts.length === 1 && url) return redirectTo(url);
    return <MovedResourcePage resource={node} />;
  }

  const parents = node.context?.parents || [];
  const subjectType = getSubjectType(node.context?.rootId);

  if (subjectType === "multiDisciplinary" && parents.length === 3) {
    return <MultidisciplinarySubjectArticle node={node} />;
  }

  return <TopicContainer node={node} subjectType={subjectType} />;
};

export default TopicPage;
